import React, { useState, lazy, Suspense, useCallback, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { CommandStatusBanner } from './CommandStatusBanner';
import { AccessBoundary } from './access/AccessBoundary';
import { canVisitPage } from '../services/pagePolicies';
import { useAuth } from '../context/AuthContext';
import { useSite } from '../context/SiteContext';
import { createCancelGroup, cancelGroup } from '../services/requestManager';
import {
  LayoutDashboard, FileText, Receipt, CreditCard, Wallet, 
  CheckCircle, BookOpen, BarChart3, Settings, LogOut, Menu, X,
  Building2, Users, Package, Landmark, ChevronDown, MapPin, History, Scale, Link2,
  ArrowLeftRight, AlertTriangle, TrendingUp, Send, DollarSign, ClipboardCheck,
  Shield, ClipboardList, Copy, Warehouse, IndianRupee, Database, Activity
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { ScrollArea } from '../components/ui/scroll-area';

// Lazy load support widget (company users only)
const SupportWidget = lazy(() => import('./SupportWidget'));

const Layout = () => {
  const { user, company, logout, hasPermission } = useAuth();
  const { sites, selectedSite, selectSite, clearSite } = useSite();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // ── WEB6P: Cancel all pending requests on route change ────────────────
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      cancelGroup('page');                       // abort any in-flight page requests
      prevPathRef.current = location.pathname;
    }
    createCancelGroup('page');                   // fresh group for the new page
    return () => cancelGroup('page');            // cleanup on unmount
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    { 
      path: '/dashboard', 
      label: 'Dashboard', 
      icon: LayoutDashboard,
      permission: null 
    },
    { 
      path: '/challans', 
      label: 'Challans', 
      icon: FileText,
      permission: 'challan_view' 
    },
    { 
      path: '/bills', 
      label: 'Bills', 
      icon: Receipt,
      permission: 'bill_view' 
    },
    { 
      path: '/payments', 
      label: 'Payments', 
      icon: CreditCard,
      permission: 'payment_view' 
    },
    { 
      path: '/expenses', 
      label: 'Daily Kharch', 
      icon: Wallet,
      permission: 'expense_view' 
    },
    { 
      path: '/receipts', 
      label: 'Govt Receipts', 
      icon: Landmark,
      permission: 'receipt_view' 
    },
    {
      path: '/site-transfers',
      label: 'Site Transfers',
      icon: ArrowLeftRight,
      permission: null
    },
    {
      path: '/work-programme/reports/real-stock-status',
      label: 'Real Stock Status',
      icon: Warehouse,
      permission: null
    },
    {
      path: '/other-income',
      label: 'Income from Other Source',
      icon: TrendingUp,
      permission: null
    },
    {
      path: '/budget',
      label: 'Budget',
      icon: IndianRupee,
      permission: null
    },
    {
      path: '/other-expenses',
      label: 'Other Expenses',
      icon: Wallet,
      permission: null
    },
    {
      path: '/outstanding/payables',
      label: 'Payables',
      icon: AlertTriangle,
      permission: 'report_view'
    },
    {
      path: '/outstanding/receivables',
      label: 'Receivables',
      icon: TrendingUp,
      permission: 'report_view'
    },
    { 
      path: '/checking', 
      label: 'Checking', 
      icon: CheckCircle,
      permission: 'checking_view' 
    },
    { 
      path: '/ledgers', 
      label: 'Ledgers', 
      icon: BookOpen,
      permission: 'ledger_view' 
    },
    { 
      path: '/reports/hub', 
      label: 'Reports Hub', 
      icon: BarChart3,
      permission: 'report_view' 
    },
    { 
      path: '/reports', 
      label: 'Quick Reports', 
      icon: BarChart3,
      permission: 'report_view' 
    },
    { 
      path: '/reports/advanced', 
      label: 'Full Reports (60)', 
      icon: BarChart3,
      permission: 'report_view' 
    },
    { 
      path: '/reports/expenses', 
      label: 'Expense Reports', 
      icon: Wallet,
      permission: 'report_view' 
    },
    { 
      path: '/reports/inventory', 
      label: 'Inventory Reports', 
      icon: Package,
      permission: 'report_view' 
    },
    { 
      path: '/reports/detailed', 
      label: 'Detailed Reports', 
      icon: FileText,
      permission: 'report_view' 
    },
    {
      path: '/reports/site-transfers',
      label: 'Site Transfer Reports',
      icon: ArrowLeftRight,
      permission: 'report_view'
    },
    {
      path: '/reports/roznamcha',
      label: 'Roznamcha Reports',
      icon: BookOpen,
      permission: 'report_view'
    },
    {
      path: '/salary',
      label: 'Salary Entry',
      icon: DollarSign,
      permission: null
    },
    { 
      path: '/loans', 
      label: 'Loans', 
      icon: Landmark,
      permission: 'loan_manage' 
    },
    { 
      path: '/advances', 
      label: 'Staff Advances', 
      icon: Wallet,
      permission: 'advance_create' 
    },
    { 
      path: '/audit-trail', 
      label: 'Audit Trail', 
      icon: History,
      permission: 'audit_view' 
    },
    { 
      path: '/inventory', 
      label: 'Inventory', 
      icon: Package,
      permission: null
    },
  ];

  const settingsItems = [
    { path: '/admin/access-control', label: 'Access Control', icon: Shield, permission: 'admin.access_control.view' },
    { path: '/admin/audit-logs', label: 'Global History', icon: History, permission: 'admin.audit.view' },
    { path: '/settings/projects', label: 'Projects', icon: Building2, permission: 'master_project' },
    { path: '/settings/sites', label: 'Sites', icon: MapPin, permission: 'master_site' },
    { path: '/settings/parties', label: 'Parties', icon: Users, permission: 'master_party' },
    { path: '/settings/items', label: 'Items', icon: Package, permission: 'master_item' },
    { path: '/settings/masters', label: 'Data Masters', icon: Scale, permission: 'master_item' },
    { path: '/settings/opening-balances', label: 'Opening Balances', icon: Scale, permission: 'masters.opening_balance.view' },
    { path: '/settings/mappings', label: 'Mappings (Rates)', icon: Link2, permission: 'master_item' },
    { path: '/settings/banks', label: 'Bank Accounts', icon: Landmark, permission: 'master_bank' },
    { path: '/settings/users', label: 'Users', icon: Users, permission: 'user_manage' },
    { path: '/settings/designations', label: 'Designations', icon: Settings, permission: 'designation_manage' },
    { path: '/settings/backups', label: 'Backup & Restore', icon: Shield, permission: null },
    { path: '/settings/seed-center', label: 'Seed Center', icon: Database, permission: null },
    { path: '/settings/ci-pipeline', label: 'CI Pipeline', icon: Activity, permission: null },
  ];

  const wpNavItems = [
    { path: '/work-programme', label: 'Overview', icon: ClipboardList, permission: null },
    { path: '/work-programme/reports', label: 'WP Reports', icon: BarChart3, permission: null },
    { path: '/work-programme/approvals', label: 'WP Approvals', icon: CheckCircle, permission: null },
    { path: '/work-programme/templates', label: 'Templates', icon: Copy, permission: null },
  ];

  const roznamchaItems = [
    { path: '/roznamcha/wallets', label: 'Wallets', icon: Wallet, permission: null },
    { path: '/roznamcha/transfer', label: 'Cash Transfer', icon: Send, permission: null },
    { path: '/roznamcha/expenses', label: 'Expense Review', icon: ClipboardCheck, permission: null },
  ];

  const p2Items = [
    { path: '/market-rates', label: 'Rate Intelligence', icon: TrendingUp, permission: null },
    { path: '/p2', label: 'P2 Dashboard', icon: FileText, permission: null },
    { path: '/reports/p2', label: 'P2 Reports', icon: BarChart3, permission: 'report_view' },
  ];

  const fdBgItems = [
    { path: '/fd', label: 'Fixed Deposits', icon: Landmark, permission: null },
    { path: '/bg', label: 'Bank Guarantees', icon: Shield, permission: null },
    { path: '/reports/fd-bg', label: 'FD & BG Reports', icon: BarChart3, permission: 'report_view' },
  ];

  const filteredMenuItems = menuItems.filter(
    item => canVisitPage(user, item.path)
  );

  const filteredSettingsItems = settingsItems.filter(
    item => canVisitPage(user, item.path)
  );

  const isActive = (path) => {
    // Exact match for report sub-routes to avoid multiple highlights
    if (path.startsWith('/reports/')) return location.pathname === path;
    if (path === '/reports') return location.pathname === '/reports';
    if (path.startsWith('/outstanding/')) return location.pathname === path;
    if (path.startsWith('/roznamcha/')) return location.pathname === path || location.pathname.startsWith(path + '/');
    if (path === '/p2') return location.pathname === '/p2' || location.pathname.startsWith('/p2/');
    return location.pathname.startsWith(path);
  };

  // B5: Hover prefetch map — when hovering sidebar, prefetch that route chunk
  const prefetchMap = {
    '/ledgers': () => import(/* webpackChunkName: "ledger" */ '../pages/LedgersPage'),
    '/payments': () => import(/* webpackChunkName: "payments" */ '../pages/PaymentsPage'),
    '/bills': () => import(/* webpackChunkName: "bills" */ '../pages/BillsPage'),
    '/challans': () => import('../pages/ChallansPage'),
    '/checking': () => import('../pages/CheckingPage'),
    '/expenses': () => import('../pages/ExpensesPage'),
    '/inventory': () => import('../pages/InventoryDashboardPage'),
  };
  const prefetchedRef = React.useRef(new Set());
  const handleHoverPrefetch = useCallback((path) => {
    if (prefetchedRef.current.has(path)) return;
    const fn = prefetchMap[path];
    if (fn) { prefetchedRef.current.add(path); fn(); }
  }, []); // eslint-disable-line

  const NavItem = ({ item, onClick }) => (
    <Link
      to={item.path}
      onClick={onClick}
      onMouseEnter={() => handleHoverPrefetch(item.path)}
      className={`sidebar-item ${isActive(item.path) ? 'active' : ''}`}
      data-testid={`nav-${item.path.replace('/', '')}`}
    >
      <item.icon className="w-5 h-5" />
      <span>{item.label}</span>
    </Link>
  );

  return (
    <div className="app-shell flex flex-col overflow-hidden" style={{ background: 'linear-gradient(160deg,#f8fafc 0%,#f1f5f9 50%,#e2e8f0 100%)' }}>
      {/* Mobile Header */}
      <header data-testid="mobile-app-header" className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-white border-b border-slate-200/60 px-4 py-3">
        <div className="grid grid-cols-[2.5rem_minmax(0,1fr)_2.5rem] items-center gap-3 min-w-0">
          <button
            type="button"
            aria-label="Open navigation"
            aria-expanded={sidebarOpen}
            aria-controls="app-sidebar"
            onClick={() => setSidebarOpen(true)}
            className="p-2 shrink-0 hover:bg-slate-100 rounded-lg"
            style={{ pointerEvents: 'auto', touchAction: 'manipulation' }}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6 text-slate-600" />
          </button>
          <h1 data-testid="mobile-company-name" className="min-w-0 truncate font-bold text-lg text-slate-800" style={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
            {company?.name || 'ERP'}
          </h1>
          <div className="w-10 shrink-0" />
        </div>
      </header>

      {/* Sidebar */}
      <aside id="app-sidebar" data-testid="app-sidebar"
        className={`fixed inset-y-0 left-0 z-40 w-72 overflow-hidden transform transition-transform duration-300 lg:block lg:translate-x-0 lg:visible ${
          sidebarOpen ? 'block translate-x-0 visible' : 'hidden -translate-x-full invisible'
        }`}
        style={{ background: 'linear-gradient(180deg,#ffffff 0%,#f8fafc 100%)', borderRight: '1px solid rgba(13,148,136,0.12)', boxShadow: '4px 0 24px rgba(13,148,136,0.07)' }}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 shrink-0 border-b border-slate-200/60">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <h1 data-testid="sidebar-company-name" className="break-words text-xl font-bold text-slate-800">
                  {company?.name || 'Site Pe'}
                </h1>
                <p data-testid="sidebar-company-code" className="text-sm text-slate-500 mt-0.5">Code: {company?.code}</p>
              </div>
              <button type="button" aria-label="Close navigation" data-testid="mobile-menu-close-button"
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden shrink-0 p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
          </div>

          {/* Site Selector */}
          <div className="p-4 border-b border-slate-200/60">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  className="w-full justify-between h-12 rounded-xl border-slate-200 hover:border-sky-400 hover:bg-sky-50/50"
                  data-testid="site-selector"
                >
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-sky-500" />
                    <span className="truncate">
                      {selectedSite ? selectedSite.name : 'Select Site'}
                    </span>
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64">
                {sites.map((site) => (
                  <DropdownMenuItem
                    key={site.id}
                    onClick={() => selectSite(site)}
                    className={`cursor-pointer ${selectedSite?.id === site.id ? 'bg-sky-50 text-sky-700' : ''}`}
                    data-testid={`site-option-${site.id}`}
                  >
                    <div className="flex items-center gap-2">
                      {site.is_office ? (
                        <Building2 className="w-4 h-4 text-pink-500" />
                      ) : (
                        <MapPin className="w-4 h-4 text-sky-500" />
                      )}
                      <span>{site.name}</span>
                    </div>
                  </DropdownMenuItem>
                ))}
                {selectedSite && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={clearSite} className="text-slate-500">
                      Clear Selection
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 min-h-0 p-4">
            <nav className="space-y-1">
              {filteredMenuItems.map((item) => (
                <NavItem 
                  key={item.path} 
                  item={item} 
                  onClick={() => setSidebarOpen(false)}
                />
              ))}

              {/* Roznamcha Section */}
              <div className="pt-6 pb-2">
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-4">
                  Roznamcha
                </span>
              </div>
              {roznamchaItems.filter(item => canVisitPage(user, item.path)).map((item) => (
                <NavItem 
                  key={item.path} 
                  item={item}
                  onClick={() => setSidebarOpen(false)}
                />
              ))}

              {/* P2 Section */}
              <div className="pt-6 pb-2">
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-4">
                  P2
                </span>
              </div>
              {p2Items.filter(item => canVisitPage(user, item.path)).map((item) => (
                <NavItem 
                  key={item.path} 
                  item={item}
                  onClick={() => setSidebarOpen(false)}
                />
              ))}

              {/* FD & BG Section */}
              <div className="pt-6 pb-2">
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-4">
                  FD & BG
                </span>
              </div>
              {fdBgItems.filter(item => canVisitPage(user, item.path)).map((item) => (
                <NavItem 
                  key={item.path} 
                  item={item}
                  onClick={() => setSidebarOpen(false)}
                />
              ))}

              {filteredSettingsItems.length > 0 && (
                <>
                  <div className="pt-6 pb-2">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-4">
                      Settings
                    </span>
                  </div>
                  {filteredSettingsItems.map((item) => (
                    <NavItem 
                      key={item.path} 
                      item={item}
                      onClick={() => setSidebarOpen(false)}
                    />
                  ))}
                </>
              )}

              {/* Work Programme Section */}
              <div className="pt-6 pb-2">
                <span className="text-xs font-semibold text-violet-400 uppercase tracking-wider px-4">
                  Work Programme
                </span>
              </div>
              {wpNavItems.filter(item => canVisitPage(user, item.path)).map((item) => (
                <NavItem 
                  key={item.path} 
                  item={item}
                  onClick={() => setSidebarOpen(false)}
                />
              ))}
            </nav>
          </ScrollArea>

          {/* User Info */}
          <div className="p-4 shrink-0 border-t border-slate-200/60">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-400 to-pink-500 flex items-center justify-center text-white font-bold">
                {user?.name?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-800 truncate">{user?.name}</p>
                <p className="text-xs text-slate-500 truncate">{user?.designation || 'User'}</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <button type="button" aria-label="Close navigation" data-testid="mobile-menu-overlay"
          className="fixed inset-0 bg-black/20 z-[35] lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-72 flex-1 min-w-0 max-w-full pt-16 lg:pt-0 overflow-y-auto scroll-owner"
        data-testid="app-main-content" data-scroll-owner="true">
        <div className="p-4 lg:p-6 min-w-0 max-w-full">
          <CommandStatusBanner />
          <AccessBoundary><Outlet /></AccessBoundary>
        </div>
      </main>
      
      {/* Support Widget for company users (not CEO) */}
      {!user?.is_ceo && (
        <Suspense fallback={null}>
          <SupportWidget />
        </Suspense>
      )}
    </div>
  );
};

export default Layout;
